// call-signals.js — validation stricte des enveloppes d'appel APRÈS déchiffrement E2E.
// Le serveur ne voit jamais ces champs : il ne relaie que ciphertext + nonce.
export const CALL_SIGNAL_VERSION = 1;
export const CALL_SIGNAL_TTL_MS = 2 * 60 * 1000;
export const CALL_ID_RE = /^[a-f0-9-]{20,64}$/i;
export const DEVICE_ID_RE = /^[a-zA-Z0-9_-]{12,80}$/;
export const CALL_ACTIONS = new Set([
  'offer', 'ringing', 'answer', 'selected', 'candidate',
  'restart-offer', 'restart-answer',
  'decline', 'busy', 'hangup', 'cancel', 'failed',
]);

export const TERMINAL_CALL_ACTIONS = new Set(['decline', 'busy', 'hangup', 'cancel', 'failed']);

export function isTerminalCallAction(action) { return TERMINAL_CALL_ACTIONS.has(action); }

function isRecord(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function cleanString(value, max = 256) {
  return typeof value === 'string' ? value.slice(0, max) : '';
}

export function normalizeCallSignal(raw, now = Date.now()) {
  if (!isRecord(raw) || raw.v !== CALL_SIGNAL_VERSION) return null;
  const action = cleanString(raw.action, 32);
  const callId = cleanString(raw.callId, 80);
  const fromDeviceId = cleanString(raw.fromDeviceId, 80);
  const toDeviceId = cleanString(raw.toDeviceId, 80);
  const selectedDeviceId = cleanString(raw.selectedDeviceId, 80);
  const ts = Number(raw.ts);
  if (!CALL_ACTIONS.has(action) || !CALL_ID_RE.test(callId) || !DEVICE_ID_RE.test(fromDeviceId)) return null;
  if (toDeviceId && !DEVICE_ID_RE.test(toDeviceId)) return null;
  if (selectedDeviceId && !DEVICE_ID_RE.test(selectedDeviceId)) return null;
  if (action === 'selected' && !selectedDeviceId) return null;
  if (!Number.isFinite(ts) || Math.abs(now - ts) > CALL_SIGNAL_TTL_MS + 30000) return null;

  const media = raw.media === 'video' ? 'video' : raw.media === 'audio' ? 'audio' : '';
  const out = { v: CALL_SIGNAL_VERSION, action, callId, fromDeviceId, ts };
  if (media) out.media = media;
  if (toDeviceId) out.toDeviceId = toDeviceId;
  if (selectedDeviceId) out.selectedDeviceId = selectedDeviceId;

  if (['offer', 'answer', 'restart-offer', 'restart-answer'].includes(action)) {
    const description = raw.description;
    const expected = action.endsWith('offer') ? 'offer' : 'answer';
    if (!isRecord(description) || description.type !== expected || typeof description.sdp !== 'string') return null;
    if (description.sdp.length < 10 || description.sdp.length > 120000) return null;
    out.description = { type: expected, sdp: description.sdp };
  }
  if (action === 'candidate') {
    const candidate = raw.candidate;
    if (!isRecord(candidate) || typeof candidate.candidate !== 'string' || candidate.candidate.length > 4096) return null;
    out.candidate = {
      candidate: candidate.candidate,
      sdpMid: typeof candidate.sdpMid === 'string' ? candidate.sdpMid.slice(0, 128) : null,
      sdpMLineIndex: Number.isInteger(candidate.sdpMLineIndex) && candidate.sdpMLineIndex >= 0 && candidate.sdpMLineIndex <= 32 ? candidate.sdpMLineIndex : null,
      usernameFragment: typeof candidate.usernameFragment === 'string' ? candidate.usernameFragment.slice(0, 256) : undefined,
    };
  }
  if (['decline', 'busy', 'hangup', 'cancel', 'failed'].includes(action)) {
    out.reason = cleanString(raw.reason, 160);
  }
  return out;
}

export function makeCallSignal({ action, callId, fromDeviceId, toDeviceId, selectedDeviceId, description, candidate, reason, media, now = Date.now() }) {
  const raw = {
    v: CALL_SIGNAL_VERSION,
    action,
    callId,
    fromDeviceId,
    ts: now,
    ...(toDeviceId ? { toDeviceId } : {}),
    ...(selectedDeviceId ? { selectedDeviceId } : {}),
    ...(description ? { description } : {}),
    ...(candidate ? { candidate } : {}),
    ...(reason ? { reason } : {}),
    ...(media === 'video' || media === 'audio' ? { media } : {}),
  };
  const normalized = normalizeCallSignal(raw, now);
  if (!normalized) throw new TypeError('Signal d’appel invalide.');
  return normalized;
}

export function signalTargetsDevice(signal, deviceId) {
  return !signal?.toDeviceId || signal.toDeviceId === deviceId;
}


// Filtre les candidats ICE par appareil une fois qu'un appareil distant a été
// choisi. C'est indispensable pour les comptes ouverts sur plusieurs
// appareils : les candidats d'un deuxième téléphone qui aurait répondu en
// parallèle ne doivent jamais être injectés dans la RTCPeerConnection retenue.
export function candidateComesFromSelectedDevice(signal, selectedDeviceId) {
  if (!signal || signal.action !== 'candidate') return false;
  return !selectedDeviceId || signal.fromDeviceId === selectedDeviceId;
}

export function candidatesForSelectedDevice(entries, selectedDeviceId) {
  if (!Array.isArray(entries)) return [];
  return entries
    .filter((entry) => entry && (!selectedDeviceId || entry.fromDeviceId === selectedDeviceId))
    .map((entry) => entry.candidate)
    .filter(Boolean);
}


// Propriétaire de l'acquittement store-and-forward :
// - un signal ciblé n'est acquitté que par l'appareil ciblé ;
// - `selected` est lu par tous les appareils, mais seul l'appareil retenu
//   l'acquitte pour préserver la décision en cas de reconnexion transitoire.
export function shouldAcknowledgeCallSignal(signal, deviceId) {
  if (!signalTargetsDevice(signal, deviceId)) return false;
  if (signal?.action === 'selected') return signal.selectedDeviceId === deviceId;
  return true;
}
